/**
 * CP-10.3 — Support Snapshot Panel.
 *
 * Read-only diagnostic surface for super admins.
 * Per CP-10.3 contract + 3 user-added locks:
 *   - Performance Lock:   ONE backend call (`GET /admin/users/{uuid}/support-snapshot`).
 *   - Isolation Lock:     No buttons, no actions — context, not workflow.
 *   - Financial Lock:     Counts only; never display dollar values here.
 *
 * Surfaces:
 *   - Open dispute count
 *   - Active financial holds
 *   - Credit memo count
 *   - Reachability classification + score
 *   - Last verification resend (timestamp + 30-day rolling count)
 *   - Last 5 dispute transitions
 *   - Last 5 credit memos (state + linked dispute id + issued_at — no money)
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, ShieldAlert, Mailbox, FileText,
         AlertCircle, Clock, Copy } from "lucide-react";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function fmt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch { return iso; }
}

function CountTile({ testId, label, value, tone, icon }) {
  const tones = {
    rose:    { ring: "ring-rose-200",    bg: "bg-rose-50",    txt: "text-rose-700" },
    amber:   { ring: "ring-amber-200",   bg: "bg-amber-50",   txt: "text-amber-700" },
    emerald: { ring: "ring-emerald-200", bg: "bg-emerald-50", txt: "text-emerald-700" },
    slate:   { ring: "ring-slate-200",   bg: "bg-slate-50",   txt: "text-slate-700" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div data-testid={testId}
         className={`flex flex-col gap-1 rounded-lg ring-1 ${t.ring} ${t.bg} p-4`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
        {icon}{label}
      </div>
      <div className={`text-2xl font-semibold ${t.txt}`}>{value}</div>
    </div>
  );
}

function ClassificationPill({ classification }) {
  const map = {
    verified:              "bg-emerald-100 text-emerald-800",
    pending_verification:  "bg-amber-100 text-amber-800",
    bounced:               "bg-rose-100 text-rose-800",
    complained:            "bg-rose-100 text-rose-800",
    unreachable:           "bg-rose-100 text-rose-800",
    suppressed:            "bg-slate-200 text-slate-800",
    inactive:              "bg-slate-100 text-slate-600",
    unknown:               "bg-slate-100 text-slate-600",
  };
  return (
    <span data-testid="snap-reachability-class"
          className={`text-xs px-2 py-0.5 rounded ${map[classification] || map.unknown}`}>
      {classification}
    </span>
  );
}

export default function SupportSnapshot() {
  const { uuid } = useParams();
  const { user } = useAuth();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api(`/admin/users/${uuid}/support-snapshot`)
      .then((s) => { if (active) setSnap(s); })
      .catch((e) => toast.error(e.problem?.title || e.message))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [uuid]);

  // CP-10.4 polish — Copy snapshot to clipboard.
  // Client-side ONLY. No API call. No mutation. Copies the data already on
  // screen plus a timestamp + viewing admin's uuid (available from auth ctx).
  const copySnapshot = async () => {
    if (!snap) return;
    const payload = {
      generated_at: new Date().toISOString(),
      viewed_by_admin_uuid: user?.uuid || null,
      ...snap,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopied(true);
      toast.success("Snapshot copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not access clipboard");
    }
  };

  if (loading) {
    return <Layout><div className="p-6 text-slate-500">Loading snapshot…</div></Layout>;
  }
  if (!snap) {
    return <Layout><div className="p-6 text-slate-500">No data.</div></Layout>;
  }

  const score = snap.reachability.score;
  const scoreTone =
    score == null ? "slate" :
    score >= 85   ? "emerald" :
    score >= 60   ? "amber" : "rose";

  return (
    <Layout>
      <div data-testid="support-snapshot-page"
           className="max-w-5xl mx-auto p-6 space-y-6">
        <Link to="/app/admin" className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
          <ChevronLeft className="h-4 w-4" /> Admin
        </Link>

        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Support snapshot</h1>
            <div className="mt-1 text-sm text-slate-500"
                 data-testid="snap-user-email">
              {snap.user.email} <span className="text-slate-400">· uuid {snap.user.uuid}</span>
            </div>
            <div className="mt-1 text-[11px] uppercase tracking-wide text-slate-400">
              Read-only · context for triage · counts only, no financial detail
            </div>
          </div>
          <button
            type="button"
            data-testid="snap-copy-button"
            onClick={copySnapshot}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-sm text-slate-700 transition shrink-0"
            title="Copy snapshot to clipboard (JSON; client-side only; includes timestamp + your admin uuid)"
          >
            <Copy className="h-3.5 w-3.5" />
            {copied ? "Copied" : "Copy snapshot"}
          </button>
        </header>

        {/* Counts — Financial Visibility Lock: numbers only */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <CountTile testId="snap-count-open-disputes"
                     label="Open disputes"
                     value={snap.counts.open_disputes}
                     tone={snap.counts.open_disputes > 0 ? "rose" : "emerald"}
                     icon={<ShieldAlert className="h-3.5 w-3.5" />} />
          <CountTile testId="snap-count-active-holds"
                     label="Active financial holds"
                     value={snap.counts.active_financial_holds}
                     tone={snap.counts.active_financial_holds > 0 ? "amber" : "emerald"}
                     icon={<AlertCircle className="h-3.5 w-3.5" />} />
          <CountTile testId="snap-count-credit-memos"
                     label="Credit memos issued"
                     value={snap.counts.credit_memos}
                     tone="slate"
                     icon={<FileText className="h-3.5 w-3.5" />} />
        </section>

        {/* Reachability + Resend */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div data-testid="snap-reachability-card"
               className="bg-white rounded-lg border border-slate-200 p-5">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-3 flex items-center gap-2">
              <Mailbox className="h-3.5 w-3.5" /> Reachability
            </div>
            <div className="flex items-center gap-3 text-sm">
              <ClassificationPill classification={snap.reachability.classification} />
              <span data-testid="snap-reachability-score"
                    className={`text-xs px-2 py-0.5 rounded ${
                      scoreTone === "emerald" ? "bg-emerald-100 text-emerald-800" :
                      scoreTone === "amber"   ? "bg-amber-100 text-amber-800" :
                      scoreTone === "rose"    ? "bg-rose-100 text-rose-800" :
                                                "bg-slate-100 text-slate-600"
                    }`}>
                {score == null ? "—" : `${score}%`}
              </span>
              <span className="text-xs text-slate-400">
                computed {fmt(snap.reachability.computed_at)}
              </span>
            </div>
          </div>

          <div data-testid="snap-resend-card"
               className="bg-white rounded-lg border border-slate-200 p-5">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-3 flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" /> Verification resend
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">Last resend</div>
                <div data-testid="snap-resend-last-at"
                     className="text-slate-700">
                  {fmt(snap.verification_resend.last_resend_at)}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">
                  Rolling ({snap.verification_resend.rolling_window_days}d)
                </div>
                <div data-testid="snap-resend-rolling"
                     className="text-slate-700">
                  {snap.verification_resend.rolling_count_30d}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Recent disputes (last 5) */}
        <section data-testid="snap-recent-disputes"
                 className="bg-white rounded-lg border border-slate-200">
          <div className="px-4 py-3 border-b text-sm font-semibold text-slate-700">
            Recent disputes (last 5)
          </div>
          {snap.recent_disputes.length === 0 ? (
            <div className="p-6 text-sm text-slate-400">No dispute activity.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-slate-500 border-b">
                <tr>
                  <th className="px-4 py-2">Dispute</th>
                  <th className="px-4 py-2">Subject</th>
                  <th className="px-4 py-2">Transition</th>
                  <th className="px-4 py-2">Reason</th>
                  <th className="px-4 py-2">Actor</th>
                  <th className="px-4 py-2">When</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {snap.recent_disputes.map((d, i) => (
                  <tr key={`${d.dispute_id}-${d.occurred_at}-${i}`}
                      data-testid={`snap-dispute-row-${d.dispute_id}`}>
                    <td className="px-4 py-2 font-mono text-xs">#{d.dispute_id}</td>
                    <td className="px-4 py-2 text-slate-600">{d.subject_kind}</td>
                    <td className="px-4 py-2 text-xs text-slate-600">
                      {d.from_state || "—"} → <span className="font-semibold">{d.to_state}</span>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">{d.reason_code}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{d.actor_kind}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{fmt(d.occurred_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Recent credit memos (last 5) — Financial Visibility Lock: NO $ */}
        <section data-testid="snap-recent-memos"
                 className="bg-white rounded-lg border border-slate-200">
          <div className="px-4 py-3 border-b text-sm font-semibold text-slate-700">
            Recent credit memos (last 5) <span className="text-xs text-slate-400 ml-2">amounts intentionally hidden</span>
          </div>
          {snap.recent_credit_memos.length === 0 ? (
            <div className="p-6 text-sm text-slate-400">No credit memos.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-slate-500 border-b">
                <tr>
                  <th className="px-4 py-2">Memo</th>
                  <th className="px-4 py-2">Dispute</th>
                  <th className="px-4 py-2">State</th>
                  <th className="px-4 py-2">Reason</th>
                  <th className="px-4 py-2">Issued</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {snap.recent_credit_memos.map((m) => (
                  <tr key={m.credit_memo_id}
                      data-testid={`snap-memo-row-${m.credit_memo_id}`}>
                    <td className="px-4 py-2 font-mono text-xs">#{m.credit_memo_id}</td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {m.dispute_id ? `#${m.dispute_id}` : "—"}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <span className={`px-2 py-0.5 rounded ${
                        m.state === "issued"  ? "bg-emerald-100 text-emerald-800" :
                        m.state === "voided"  ? "bg-slate-200 text-slate-700" :
                                                "bg-amber-100 text-amber-800"
                      }`}>{m.state}</span>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">{m.reason_code}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{fmt(m.issued_at || m.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </Layout>
  );
}
